import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV, CONTACT } from "@/data/site";

const Wordmark = () => (
  <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-3 leading-none">
    <span className="font-serif font-semibold text-[22px] sm:text-[26px] tracking-[0.32em] text-rossi-cream">ROSSI</span>
    <span className="serif-italic text-lg sm:text-xl text-rossi-gold transition-transform duration-700 group-hover:translate-x-1">Ceramiche</span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);

  return (
    <header data-testid="navbar" className={`fixed top-0 inset-x-0 z-50 transition-colors duration-700 ${scrolled ? "glass" : "bg-rossi-bg/0"}`}>
      <div className={`container-rossi grid grid-cols-12 items-end transition-all duration-700 ${scrolled ? "pt-3 pb-2" : "pt-6 pb-4"}`}>
        <div className="col-span-8 lg:col-span-4"><Wordmark /></div>
        <p className="hidden lg:block col-span-4 text-[11px] font-mono uppercase tracking-[0.24em] text-rossi-stone">Tiling &middot; Bathrooms &middot; Surfaces &mdash; Stockport</p>
        <div className="col-span-4 flex justify-end items-center gap-5">
          <a href={CONTACT.phoneHref} data-testid="nav-call" className="hidden lg:inline text-[12px] font-mono tracking-[0.12em] text-rossi-cream hover:text-rossi-gold transition-colors">{CONTACT.phone}</a>
          <Link to="/studio" data-testid="nav-start-design" className="hidden lg:inline-flex btn-gold !py-2.5 !px-4">The Studio <ArrowUpRight size={14} /></Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-toggle" className="lg:hidden text-rossi-cream p-1" aria-label="Open menu"><Menu size={24} strokeWidth={1.5} /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-rossi-bg border-l border-rossi-cream/15 w-[310px] p-0">
              <div className="p-7 flex flex-col h-full">
                <Wordmark />
                <nav className="flex flex-col mt-10 border-t border-rossi-cream/15">
                  {NAV.map((n, i) => (
                    <NavLink key={n.to} to={n.to} data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) => `flex items-baseline gap-4 py-3.5 border-b border-rossi-cream/10 text-lg ${isActive ? "text-rossi-gold" : "text-rossi-cream"}`}>
                      <span className="text-[10px] font-mono text-rossi-stone">{String(i + 1).padStart(2, "0")}</span>{n.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="mt-auto flex flex-col gap-3 pt-6">
                  <Link to="/studio" className="btn-gold w-full">Enter The Studio</Link>
                  <a href={CONTACT.phoneHref} className="btn-ghost-gold w-full">Call {CONTACT.phone}</a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="hidden lg:block border-t border-rossi-cream/15">
        <nav className="container-rossi flex justify-between">
          {NAV.map((n, i) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `group relative py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors duration-500 ${isActive ? "text-rossi-gold" : "text-rossi-cream/75 hover:text-rossi-cream"}`}>
              {({ isActive }) => (
                <>
                  <span className="font-mono text-[9px] text-rossi-stone mr-1.5">{String(i + 1).padStart(2, "0")}</span>{n.label}
                  <span className={`absolute left-0 right-0 -top-px h-px bg-rossi-gold origin-left transition-transform duration-700 ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="border-b border-rossi-cream/15" />
    </header>
  );
};

export default Navbar;
